"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLenis } from "@/components/providers/SmoothScrollProvider";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
import { StepShell } from "./StepShell";
import { ChooseServiceStep } from "./steps/ChooseServiceStep";
import { RegisterIntroStep } from "./steps/RegisterIntroStep";
import { CompanyNameStep } from "./steps/CompanyNameStep";
import { RegisterTimelineStep } from "./steps/RegisterTimelineStep";
import { CompanySecretaryStep } from "./steps/CompanySecretaryStep";
import { RegisteredAddressStep } from "./steps/RegisteredAddressStep";
import { AccountingSupportStep } from "./steps/AccountingSupportStep";
import { AuditTaxStep } from "./steps/AuditTaxStep";
import { BusinessBankingStep } from "./steps/BusinessBankingStep";
import { PackageReadyStep } from "./steps/PackageReadyStep";
import { ContactFormStep } from "./steps/ContactFormStep";
import { ManageHelpStep } from "./steps/ManageHelpStep";
import { SwitchIntroStep } from "./steps/SwitchIntroStep";
import { ExistingCompanyNameStep } from "./steps/ExistingCompanyNameStep";
import { ManageSecretaryStep } from "./steps/ManageSecretaryStep";
import { ManagePastReturnsStep } from "./steps/ManagePastReturnsStep";
import { ManageRemediationStep } from "./steps/ManageRemediationStep";
import { ManageAccountingStep } from "./steps/ManageAccountingStep";
import { ManagePayrollReturnsStep } from "./steps/ManagePayrollReturnsStep";
import { ManageAccountingRemediationStep } from "./steps/ManageAccountingRemediationStep";
import { ManageAuditReturnsStep } from "./steps/ManageAuditReturnsStep";
import { ManageAuditRemediationStep } from "./steps/ManageAuditRemediationStep";
import { buildManageSummary, buildPackageSummary } from "./pricing";
import { submitGetStarted } from "./submit";
import {
  GET_STARTED_STORAGE_KEY,
  GET_STARTED_STORAGE_VERSION,
  readGetStartedStorage,
} from "./session";

export type ServiceId = "register-business" | "manage-existing-business";

export interface GetStartedData {
  serviceId: ServiceId | null;
  companyName: string;
  /** True when the user chose "Skip for now" on the company name step. */
  companyNameSkipped: boolean;
  registerTimeline: string | null;
  shareholders: string;
  secretaryPlan: string | null;
  registeredAddress: string | null;
  monthlyExpenses: string;
  accountingPlan: string | null;
  auditPlan: string | null;
  /** Banking is perk-based, so this carries no charge into the summary. */
  bankingPartner: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  /** "Manage existing business" branch: self-serve checkout vs. an advisor. */
  manageOption: string | null;
  /**
   * Whether they want help filing outstanding annual returns for previous
   * years. Carries no charge yet — the design shows no price for it.
   */
  pastAnnualReturns: string | null;
  /** How many past financial years to file for, when they said yes above. */
  remediationYears: string | null;
  /**
   * The manage branch asks the same expenses question as `monthlyExpenses` but
   * starts it unanswered — its design shows the picker's placeholder, and the
   * accounting tiers stay hidden until a band is chosen. Kept separate so the
   * register branch keeps arriving with its designed default.
   */
  manageMonthlyExpenses: string;
  /**
   * The accounting counterpart to `pastAnnualReturns`: help with outstanding
   * employer's returns and management accounts for past years.
   */
  pastPayrollReturns: string | null;
  /** How many past financial years of books to bring up to date. */
  accountingRemediationYears: string | null;
  /**
   * The audit counterpart to `pastAnnualReturns` and `pastPayrollReturns`:
   * help with audited financial statements for past years.
   */
  pastAuditStatements: string | null;
  /** How many past financial years need auditing. */
  auditRemediationYears: string | null;
  /**
   * The company they already run — distinct from `companyName`, which is the
   * name being *applied for* on the register branch.
   */
  existingCompanyName: string;
}

// An array rather than a bare union so a persisted step can be validated at
// runtime — a value saved before a rename would otherwise blank the flow.
// Steps beyond these are added here as their Figma screens are converted.
const STEPS = [
  "choose-service",
  "manage-help",
  "switch-intro",
  "existing-company-name",
  "manage-secretary",
  "manage-past-returns",
  "manage-remediation",
  "manage-accounting",
  "manage-payroll-returns",
  "manage-accounting-remediation",
  "manage-audit-tax",
  "manage-audit-returns",
  "manage-audit-remediation",
  "manage-registered-address",
  "manage-banking",
  "manage-contact-form",
  "register-intro",
  "company-name",
  "register-timeline",
  "company-secretary",
  "registered-address",
  "accounting-support",
  "audit-tax",
  "business-banking",
  "package-ready",
  "contact-form",
  "coming-soon",
] as const;

type Step = (typeof STEPS)[number];

// The design's progress fill was exactly one eighth of the track, sized for the
// eight screens it covered. The contact form is a ninth, so the divisor moves
// with it — otherwise the bar could never reach 100%.
const TOTAL_FORM_STEPS = 9;

/** Where Back goes from the manage branch's first numbered step. */
const MANAGE_INTRO_STEP: Step = "switch-intro";

/**
 * The manage branch's numbered screens, in order, for the answers given so far.
 *
 * Built from the data rather than written out as a fixed list because three of
 * the screens only exist on a "yes": the branch is ten screens long for someone
 * who needs no help with previous years and thirteen for someone who needs all
 * three. A fixed total would either promise steps that never arrive or number
 * the later screens wrongly.
 *
 * Doubles as the source of the Back targets, so there is one description of the
 * order rather than thirteen hand-written back links to keep in sync.
 */
function manageSequence(data: GetStartedData): Step[] {
  const steps: Step[] = ["existing-company-name", "manage-secretary", "manage-past-returns"];
  if (data.pastAnnualReturns === "yes") steps.push("manage-remediation");

  steps.push("manage-accounting", "manage-payroll-returns");
  if (data.pastPayrollReturns === "yes") steps.push("manage-accounting-remediation");

  steps.push("manage-audit-tax", "manage-audit-returns");
  if (data.pastAuditStatements === "yes") steps.push("manage-audit-remediation");

  steps.push("manage-registered-address", "manage-banking", "manage-contact-form");
  return steps;
}

const DEFAULT_DATA: GetStartedData = {
  serviceId: null,
  companyName: "",
  companyNameSkipped: false,
  registerTimeline: null,
  shareholders: "1 shareholder",
  secretaryPlan: null,
  registeredAddress: null,
  monthlyExpenses: "Up to HK$15,000",
  accountingPlan: null,
  auditPlan: null,
  bankingPartner: null,
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  manageOption: null,
  pastAnnualReturns: null,
  remediationYears: null,
  manageMonthlyExpenses: "",
  pastPayrollReturns: null,
  accountingRemediationYears: null,
  pastAuditStatements: null,
  auditRemediationYears: null,
  existingCompanyName: "",
};

const readPersisted = (): { step: Step; data: GetStartedData } | null => {
  const storage = readGetStartedStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(GET_STARTED_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      v?: number;
      step?: string;
      data?: Partial<GetStartedData>;
    };
    if (parsed.v !== GET_STARTED_STORAGE_VERSION) return null;
    if (!STEPS.includes(parsed.step as Step)) return null;

    return {
      step: parsed.step as Step,
      // Spread over the defaults so a blob written before a field was added
      // comes back with that field defined rather than undefined.
      data: { ...DEFAULT_DATA, ...parsed.data },
    };
  } catch {
    // Malformed JSON — start fresh rather than trapping the customer.
    return null;
  }
};

/**
 * Restores before the browser paints, so a refresh doesn't flash step 1 before
 * jumping to where the customer actually was. useLayoutEffect can't run during
 * SSR, hence the swap.
 */
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function GetStartedFlow() {
  const [step, setStep] = useState<Step>("choose-service");
  // Starts false on both server and client so hydration matches; the restore
  // below flips it. It also gates the writer, which would otherwise persist the
  // defaults over a saved session on the very first commit.
  const [restored, setRestored] = useState(false);
  const lenisRef = useLenis();

  /**
   * Steps swap in place rather than navigating, so the browser has no route
   * change to reset the scroll offset — leaving someone who scrolled to the
   * bottom of a long step landing mid-page on the next one. Every forward and
   * back transition goes through here so both directions start at the top.
   *
   * Routed through Lenis when it's running: it owns the scroll position in
   * wrapper:window mode, and a bare window.scrollTo leaves its internal target
   * stale, which snaps the page back on the next wheel event. The native call
   * is the fallback for reduced motion, or before the Lenis chunk lands.
   */
  const goToStep = useCallback(
    (next: Step) => {
      setStep(next);

      const lenis = lenisRef.current;
      if (lenis) lenis.scrollTo(0, { immediate: true });
      else window.scrollTo({ top: 0 });
    },
    [lenisRef],
  );
  const [data, setData] = useState<GetStartedData>(DEFAULT_DATA);

  useIsomorphicLayoutEffect(() => {
    const persisted = readPersisted();
    if (persisted) {
      setStep(persisted.step);
      setData(persisted.data);
    }
    // Batched with the two above, so the writer's first run already sees the
    // restored values rather than the defaults.
    setRestored(true);
  }, []);

  useEffect(() => {
    if (!restored) return;

    const storage = readGetStartedStorage();
    if (!storage) return;

    try {
      storage.setItem(
        GET_STARTED_STORAGE_KEY,
        JSON.stringify({ v: GET_STARTED_STORAGE_VERSION, step, data }),
      );
    } catch {
      // Quota or a private-mode write failure: losing the saved place is not
      // worth breaking the flow over.
    }
  }, [restored, step, data]);

  // Priced once, from every choice made so far, so each step's summary card
  // shows the same running total — including when the customer navigates back.
  const summary = buildPackageSummary(data);

  /**
   * Wraps a manage-branch screen in the Back control and progress bar, with its
   * position taken from the sequence the current answers imply.
   */
  const manageStep = (current: Step, children: React.ReactNode) => {
    const sequence = manageSequence(data);
    const index = sequence.indexOf(current);
    // A persisted step that the current answers no longer include would
    // otherwise number itself 0 — fall back to the first position.
    const position = index === -1 ? 0 : index;

    return (
      <StepShell
        step={position + 1}
        totalSteps={sequence.length}
        onBack={() => goToStep(position === 0 ? MANAGE_INTRO_STEP : sequence[position - 1])}
      >
        {children}
      </StepShell>
    );
  };

  if (step === "choose-service") {
    return (
      <ChooseServiceStep
        onSelect={(serviceId) => {
          setData((d) => ({ ...d, serviceId }));
          goToStep(serviceId === "register-business" ? "register-intro" : "manage-help");
        }}
      />
    );
  }

  if (step === "manage-help") {
    return (
      <ManageHelpStep
        onSelect={(manageOption) => {
          setData((d) => ({ ...d, manageOption }));

          if (manageOption === "expert") {
            // Hands off to the same WhatsApp thread as every other "talk to a
            // specialist" CTA on the site, rather than continuing in-app.
            openWhatsAppWithUtm(WA_NUMBERS);
            return;
          }

          goToStep("switch-intro");
        }}
      />
    );
  }

  if (step === "switch-intro") {
    return <SwitchIntroStep onStart={() => goToStep("existing-company-name")} />;
  }

  if (step === "existing-company-name") {
    return manageStep("existing-company-name", (
      <ExistingCompanyNameStep
        value={data.existingCompanyName}
        onChange={(existingCompanyName) => setData((d) => ({ ...d, existingCompanyName }))}
        onContinue={() => goToStep("manage-secretary")}
      />
    ));
  }

  if (step === "manage-secretary") {
    return manageStep("manage-secretary", (
      <ManageSecretaryStep
        shareholders={data.shareholders}
        onShareholdersChange={(shareholders) => setData((d) => ({ ...d, shareholders }))}
        plan={data.secretaryPlan}
        onPlanChange={(secretaryPlan) => setData((d) => ({ ...d, secretaryPlan }))}
        // This branch prices without the incorporation fees, so it gets its own
        // summary rather than the register branch's running total.
        summary={buildManageSummary(data)}
        onNext={() => goToStep("manage-past-returns")}
      />
    ));
  }

  if (step === "manage-past-returns") {
    return manageStep("manage-past-returns", (
      <ManagePastReturnsStep
        value={data.pastAnnualReturns}
        onChange={(pastAnnualReturns) =>
          // Dropping the year count on "no" keeps the summary honest: the
          // remediation line must not survive a change of mind.
          setData((d) => ({
            ...d,
            pastAnnualReturns,
            remediationYears: pastAnnualReturns === "yes" ? d.remediationYears : null,
          }))
        }
        summary={buildManageSummary(data)}
        // "No" skips the remediation screen and goes straight to accounting.
        onNext={() =>
          goToStep(data.pastAnnualReturns === "yes" ? "manage-remediation" : "manage-accounting")
        }
      />
    ));
  }

  if (step === "manage-remediation") {
    return manageStep("manage-remediation", (
      <ManageRemediationStep
        years={data.remediationYears}
        onYearsChange={(remediationYears) => setData((d) => ({ ...d, remediationYears }))}
        summary={buildManageSummary(data)}
        onNext={() => goToStep("manage-accounting")}
      />
    ));
  }

  if (step === "manage-accounting") {
    return manageStep("manage-accounting", (
      <ManageAccountingStep
        monthlyExpenses={data.manageMonthlyExpenses}
        onMonthlyExpensesChange={(manageMonthlyExpenses) =>
          setData((d) => ({ ...d, manageMonthlyExpenses }))
        }
        accountingPlanId={data.accountingPlan}
        onAccountingPlanChange={(accountingPlan) => setData((d) => ({ ...d, accountingPlan }))}
        summary={buildManageSummary(data)}
        onNext={() => goToStep("manage-payroll-returns")}
        onSkip={() => {
          // "Skip this" leaves accounting out of the package but still
          // continues to the employer's-returns question: help with past years
          // stands on its own, whether or not they take a forward plan. Same
          // rule as the audit screen's "No thanks".
          setData((d) => ({ ...d, accountingPlan: null }));
          goToStep("manage-payroll-returns");
        }}
      />
    ));
  }

  if (step === "manage-payroll-returns") {
    return manageStep("manage-payroll-returns", (
      <ManagePayrollReturnsStep
        value={data.pastPayrollReturns}
        onChange={(pastPayrollReturns) =>
          // Dropping the year count on "no" keeps the summary honest.
          setData((d) => ({
            ...d,
            pastPayrollReturns,
            accountingRemediationYears:
              pastPayrollReturns === "yes" ? d.accountingRemediationYears : null,
          }))
        }
        summary={buildManageSummary(data)}
        // "No" skips the remediation screen and goes straight to audit & tax.
        onNext={() =>
          goToStep(
            data.pastPayrollReturns === "yes" ? "manage-accounting-remediation" : "manage-audit-tax",
          )
        }
      />
    ));
  }

  if (step === "manage-accounting-remediation") {
    return manageStep("manage-accounting-remediation", (
      <ManageAccountingRemediationStep
        years={data.accountingRemediationYears}
        onYearsChange={(accountingRemediationYears) =>
          setData((d) => ({ ...d, accountingRemediationYears }))
        }
        summary={buildManageSummary(data)}
        // Both accounting paths rejoin at audit & tax, mirroring how the two
        // secretary paths rejoined at accounting.
        onNext={() => goToStep("manage-audit-tax")}
        onSkip={() => {
          // "Skip this" declines the remediation entirely, so the year count
          // and the answer that led here both go.
          setData((d) => ({
            ...d,
            accountingRemediationYears: null,
            pastPayrollReturns: "no",
          }));
          goToStep("manage-audit-tax");
        }}
      />
    ));
  }

  if (step === "manage-audit-tax") {
    return manageStep("manage-audit-tax", (
      <AuditTaxStep
        monthlyExpenses={data.manageMonthlyExpenses}
        onMonthlyExpensesChange={(manageMonthlyExpenses) =>
          setData((d) => ({ ...d, manageMonthlyExpenses }))
        }
        auditPlanId={data.auditPlan}
        onAuditPlanChange={(auditPlan) => setData((d) => ({ ...d, auditPlan }))}
        summary={buildManageSummary(data)}
        intro="Audit of financial statements is mandatory for all active companies in Hong Kong. MuteTaxes makes audit and Profits Tax filing simple with a fully online process handled by Hong Kong Practising CPAs."
        note={
          <div className="flex w-full flex-col items-start gap-2">
            <h3 className="font-inter text-sm font-bold text-white">
              Not sure which plan applies?
            </h3>
            <p className="font-inter text-[13px] leading-[19.5px] text-[#9CA3AF]">
              If your business has assets exceeding HK$10M, complex assets, or offshore tax
              arrangements, Premium is recommended. MuteTaxes will review your details and confirm
              the correct plan before the audit begins.
            </p>
          </div>
        }
        skipLabel="No thanks"
        compactNext
        onNext={() => goToStep("manage-audit-returns")}
        onSkip={() => {
          // Unlike skipping accounting, "No thanks" here still continues to
          // the audit-returns question: help with past audited statements
          // stands on its own, whether or not they take a forward audit plan.
          setData((d) => ({ ...d, auditPlan: null }));
          goToStep("manage-audit-returns");
        }}
      />
    ));
  }

  if (step === "manage-audit-returns") {
    return manageStep("manage-audit-returns", (
      <ManageAuditReturnsStep
        value={data.pastAuditStatements}
        onChange={(pastAuditStatements) =>
          // Dropping the year count on "no" keeps the summary honest.
          setData((d) => ({
            ...d,
            pastAuditStatements,
            auditRemediationYears:
              pastAuditStatements === "yes" ? d.auditRemediationYears : null,
          }))
        }
        summary={buildManageSummary(data)}
        // "No" skips the remediation screen and goes straight to the address.
        onNext={() =>
          goToStep(
            data.pastAuditStatements === "yes"
              ? "manage-audit-remediation"
              : "manage-registered-address",
          )
        }
      />
    ));
  }

  if (step === "manage-audit-remediation") {
    return manageStep("manage-audit-remediation", (
      <ManageAuditRemediationStep
        years={data.auditRemediationYears}
        onYearsChange={(auditRemediationYears) =>
          setData((d) => ({ ...d, auditRemediationYears }))
        }
        summary={buildManageSummary(data)}
        // Both audit paths rejoin at the registered address.
        onNext={() => goToStep("manage-registered-address")}
        onSkip={() => {
          setData((d) => ({ ...d, auditRemediationYears: null, pastAuditStatements: "no" }));
          goToStep("manage-registered-address");
        }}
      />
    ));
  }

  if (step === "manage-registered-address") {
    return manageStep("manage-registered-address", (
      <RegisteredAddressStep
        addressId={data.registeredAddress}
        onAddressChange={(registeredAddress) => setData((d) => ({ ...d, registeredAddress }))}
        summary={buildManageSummary(data)}
        wideNext
        onNext={() => goToStep("manage-banking")}
      />
    ));
  }

  if (step === "manage-banking") {
    return manageStep("manage-banking", (
      <BusinessBankingStep
        bankId={data.bankingPartner}
        onBankChange={(bankingPartner) => setData((d) => ({ ...d, bankingPartner }))}
        summary={buildManageSummary(data)}
        heading="Your bespoke incorporation package"
        intro="Based on what you told us, we suggest moving forward with the following services:"
        onSelectBank={() => goToStep("manage-contact-form")}
        onSkip={() => {
          setData((d) => ({ ...d, bankingPartner: null }));
          goToStep("manage-contact-form");
        }}
      />
    ));
  }

  if (step === "manage-contact-form") {
    return manageStep("manage-contact-form", (
      // The same contact form the register branch ends on. No StepShell here,
      // so it isn't given a step number this branch can't back out of.
      <ContactFormStep
        name={data.contactName}
        email={data.contactEmail}
        phone={data.contactPhone}
        onChange={(patch) => setData((d) => ({ ...d, ...patch }))}
        onSubmit={async () => {
          const { checkoutUrl } = await submitGetStarted(data);
          // Payment happens on Whop's hosted checkout; they come back to /get-started/payment-success.
          if (checkoutUrl) window.location.assign(checkoutUrl);
          else goToStep("coming-soon");
        }}
      />
    ));
  }

  if (step === "register-intro") {
    return <RegisterIntroStep onStart={() => goToStep("company-name")} />;
  }

  if (step === "company-name") {
    return (
      <StepShell step={1} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("register-intro")}>
        <CompanyNameStep
          value={data.companyName}
          onChange={(companyName) => setData((d) => ({ ...d, companyName, companyNameSkipped: false }))}
          onContinue={() => goToStep("register-timeline")}
          onSkip={() => {
            setData((d) => ({ ...d, companyName: "", companyNameSkipped: true }));
            goToStep("register-timeline");
          }}
        />
      </StepShell>
    );
  }

  if (step === "register-timeline") {
    return (
      <StepShell step={2} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("company-name")}>
        <RegisterTimelineStep
          value={data.registerTimeline}
          onChange={(registerTimeline) => setData((d) => ({ ...d, registerTimeline }))}
          onContinue={() => goToStep("company-secretary")}
        />
      </StepShell>
    );
  }

  if (step === "company-secretary") {
    return (
      <StepShell step={3} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("register-timeline")}>
        <CompanySecretaryStep
          shareholders={data.shareholders}
          onShareholdersChange={(shareholders) => setData((d) => ({ ...d, shareholders }))}
          plan={data.secretaryPlan}
          onPlanChange={(secretaryPlan) => setData((d) => ({ ...d, secretaryPlan }))}
          summary={summary}
          onNext={() => goToStep("registered-address")}
        />
      </StepShell>
    );
  }

  if (step === "registered-address") {
    return (
      <StepShell step={4} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("company-secretary")}>
        <RegisteredAddressStep
          addressId={data.registeredAddress}
          onAddressChange={(registeredAddress) => setData((d) => ({ ...d, registeredAddress }))}
          summary={summary}
          onNext={() => goToStep("accounting-support")}
        />
      </StepShell>
    );
  }

  if (step === "accounting-support") {
    return (
      <StepShell step={5} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("registered-address")}>
        <AccountingSupportStep
          monthlyExpenses={data.monthlyExpenses}
          onMonthlyExpensesChange={(monthlyExpenses) =>
            setData((d) => ({ ...d, monthlyExpenses }))
          }
          accountingPlanId={data.accountingPlan}
          onAccountingPlanChange={(accountingPlan) => setData((d) => ({ ...d, accountingPlan }))}
          summary={summary}
          onNext={() => goToStep("audit-tax")}
          onSkip={() => {
            // "Skip this" leaves accounting out of the package entirely.
            setData((d) => ({ ...d, accountingPlan: null }));
            goToStep("audit-tax");
          }}
        />
      </StepShell>
    );
  }

  if (step === "audit-tax") {
    return (
      <StepShell step={6} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("accounting-support")}>
        <AuditTaxStep
          monthlyExpenses={data.monthlyExpenses}
          onMonthlyExpensesChange={(monthlyExpenses) =>
            setData((d) => ({ ...d, monthlyExpenses }))
          }
          auditPlanId={data.auditPlan}
          onAuditPlanChange={(auditPlan) => setData((d) => ({ ...d, auditPlan }))}
          summary={summary}
          onNext={() => goToStep("business-banking")}
          onSkip={() => {
            setData((d) => ({ ...d, auditPlan: null }));
            goToStep("business-banking");
          }}
        />
      </StepShell>
    );
  }

  if (step === "business-banking") {
    return (
      <StepShell step={7} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("audit-tax")}>
        <BusinessBankingStep
          bankId={data.bankingPartner}
          onBankChange={(bankingPartner) => setData((d) => ({ ...d, bankingPartner }))}
          summary={summary}
          onSelectBank={() => goToStep("package-ready")}
          onSkip={() => {
            setData((d) => ({ ...d, bankingPartner: null }));
            goToStep("package-ready");
          }}
        />
      </StepShell>
    );
  }

  if (step === "package-ready") {
    return (
      <StepShell step={8} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("business-banking")}>
        {/* The review step edits every earlier answer, so it takes the whole
            state plus one patch callback rather than a prop per field. */}
        <PackageReadyStep
          data={data}
          onChange={(patch) => setData((d) => ({ ...d, ...patch }))}
          onCheckout={() => goToStep("contact-form")}
        />
      </StepShell>
    );
  }

  if (step === "contact-form") {
    return (
      <StepShell step={9} totalSteps={TOTAL_FORM_STEPS} onBack={() => goToStep("package-ready")}>
        <ContactFormStep
          name={data.contactName}
          email={data.contactEmail}
          phone={data.contactPhone}
          onChange={(patch) => setData((d) => ({ ...d, ...patch }))}
          onSubmit={async () => {
            const { checkoutUrl } = await submitGetStarted(data);
            if (checkoutUrl) window.location.assign(checkoutUrl);
            else goToStep("coming-soon");
          }}
        />
      </StepShell>
    );
  }

  return (
    <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 px-4 text-center">
      <h2 className="font-suisse-intl-bold text-3xl text-white">We&apos;ve received your request</h2>
      <p className="max-w-md font-suisse-intl-medium text-white/70">
        Check your email for your MuteTaxes dashboard login. A specialist will be in touch shortly
        to start your setup.
      </p>
      <Button
        type="button"
        variant="outline"
        onClick={() => goToStep("choose-service")}
        className="mt-2 rounded-full border-white/30 bg-transparent font-suisse-intl-medium text-white hover:bg-white/10 hover:text-white"
      >
        Back
      </Button>
    </div>
  );
}
